import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import MuiChip from '@material-ui/core/Chip';

import IconCrossBold from 'components/icons/IconCrossBold';

import hexToRgb from 'styles/hexToRgb';
import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';

const styles = theme => ({
  root: {
    transitionDelay: `${theme.transitions.duration.short}ms`,
    '&$rootSquare': {
      color: theme.palette.grey[700],
    },
  },
  rootSizeXs: {
    fontSize: pxToRem(12),
    height: 20,
    borderRadius: 12,
    '&$rootSquare': {
      borderRadius: 4,
    },
  },
  rootSizeSm: {
    height: 22,
    borderRadius: 12,
    '&$rootSquare': {
      borderRadius: 4,
    },
  },
  rootSizeMd: {
    height: 28,
    boxShadow: theme.shadows[3],
    '&$rootSquare': {
      borderRadius: 4,
    },
    '& $iconCrossBold': {
      margin: '0 12px 0 -12px',
    },
  },
  rootSizeLg: {
    height: 36,
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    boxShadow: theme.shadows[3],
    '& $iconCrossBold': {
      margin: '0 20px 0 -10px',
    },
  },
  rootBorderColorDefault: {
    border: `1px solid ${theme.palette.primary.A100}`,
  },
  rootBorderColorPrimary: {
    border: `1px solid ${theme.palette.primary[500]}`,
  },
  rootBorderColorError: {
    border: `1px solid ${theme.palette.validation[500]}`,
  },
  label: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    boxSizing: 'border-box',
    '& > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  labelSizeXs: {
    paddingLeft: 10,
    paddingRight: 10,
  },
  labelSizeSm: {
    paddingLeft: 10,
    paddingRight: 10,
  },
  labelSizeMd: {
    paddingLeft: 18,
    paddingRight: 18,
  },
  labelSizeLg: {
    paddingLeft: 25,
    paddingRight: 25,
  },
  rootColorPrimary: {
    backgroundColor: theme.palette.primary[500],
    color: theme.palette.common.white,
    '&$rootSquare': {
      color: theme.palette.common.white,
    },
  },
  rootColorPrimaryClickable: {
    '&:focus, &:hover, &:active': {
      backgroundColor: theme.palette.primary[400],
    },
  },
  rootColorPrimaryDeletable: {
    '&:focus, &:hover, &:active': {
      backgroundColor: theme.palette.primary[400],
    },
  },
  rootColorError: {
    color: theme.palette.validation[500],
  },
  rootBold: {
    fontWeight: 700,
  },
  rootSquare: {
    boxShadow: 'none',
    fontWeight: 400,
    '& $labelSizeMd': {
      paddingLeft: 10,
      paddingRight: 10,
    },
    '& $iconCrossBold': {
      margin: '0 10px 0 0',
    },
  },
  rootDeleteButtonPositionLeft: {
    flexDirection: 'row-reverse',
    '& $iconCrossBold': {
      margin: '0 -15px 0 15px',
    },
    '&$rootSizeLg $iconCrossBold': {
      margin: '0 -10px 0 20px',
    },
  },
  iconCrossBold: {
    width: 10,
    height: 10,
    margin: '0 15px 0 -15px',
    transition: theme.transitions.create(['all'], {
      duration: theme.transitions.duration.standard,
      delay: theme.transitions.duration.short,
    }),
  },
  iconCrossBoldColorError: {
    color: `rgba(${hexToRgb(theme.palette.validation[500])}, 0.54)`,
    '&:hover': {
      color: theme.palette.validation[500],
    },
  },
  iconCrossBoldColorPrimary: {
    color: theme.palette.common.white,
  },
});

const Chip = ({
  size,
  color,
  borderColor,
  bold,
  square,
  className,
  deleteButtonPosition,
  clickable,
  classes,
  ...otherProps
}) => (
  <MuiChip
    className={className}
    classes={{
      root: classNames(classes.root, {
        [classes.rootBold]: bold,
        [classes.rootSquare]: square,
        [classes.rootColorPrimary]: color === 'primary',
        [classes.rootColorPrimaryClickable]: color === 'primary' && clickable,
        [classes.rootColorPrimaryDeletable]: color === 'primary' && otherProps.onDelete,
        [classes.rootColorError]: color === 'error',
        [classes.rootSizeXs]: size === 'xs',
        [classes.rootSizeSm]: size === 'sm',
        [classes.rootSizeMd]: size === 'md',
        [classes.rootSizeLg]: size === 'lg',
        [classes.rootBorderColorDefault]: borderColor === 'default',
        [classes.rootBorderColorPrimary]: borderColor === 'primary',
        [classes.rootBorderColorError]: borderColor === 'error',
        [classes.rootDeleteButtonPositionLeft]: deleteButtonPosition === 'left',
      }),
      label: classNames(classes.label, {
        [classes.labelSizeXs]: size === 'xs',
        [classes.labelSizeSm]: size === 'sm',
        [classes.labelSizeMd]: size === 'md',
        [classes.labelSizeLg]: size === 'lg',
      }),
    }}
    deleteIcon={
      <IconCrossBold
        className={classNames(classes.iconCrossBold, {
          [classes.iconCrossBoldColorPrimary]: color === 'primary',
          [classes.iconCrossBoldColorError]: color === 'error',
        })}
      />
    }
    clickable={clickable}
    {...otherProps}
  />
);

Chip.propTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  color: PropTypes.oneOf(['primary', 'error', 'default']),
  borderColor: PropTypes.oneOf(['primary', 'error', 'default']),
  deleteButtonPosition: PropTypes.oneOf(['left', 'right']),
  bold: PropTypes.bool,
  square: PropTypes.bool,
  className: PropTypes.string,
  clickable: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootSizeSm: PropTypes.string.isRequired,
    rootSizeMd: PropTypes.string.isRequired,
    rootSizeLg: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    labelSizeSm: PropTypes.string.isRequired,
    labelSizeMd: PropTypes.string.isRequired,
    labelSizeLg: PropTypes.string.isRequired,
    rootColorPrimary: PropTypes.string.isRequired,
    rootColorPrimaryClickable: PropTypes.string.isRequired,
    rootBold: PropTypes.string.isRequired,
    rootSquare: PropTypes.string.isRequired,
    iconCrossBold: PropTypes.string.isRequired,
    iconCrossBoldColorPrimary: PropTypes.string.isRequired,
  }).isRequired,
};

Chip.defaultProps = {
  size: 'sm',
  color: 'default',
  borderColor: 'default',
  deleteButtonPosition: 'right',
  bold: true,
  className: '',
  square: false,
  clickable: false,
};

export default withStyles(styles)(Chip);



// WEBPACK FOOTER //
// ./src/components/common/Chip.js